import API from '../api';

class SellerAnalyticsService {
  constructor() {
    this.baseURL = '/analytics';
  }

  /**
   * Invia un evento analytics al backend
   */
  async trackEvent(eventData) {
    try {
      // Aggiungi informazioni aggiuntive
      const enrichedEventData = {
        ...eventData,
        page_url: window.location.href,
        user_session_id: this.getSessionId(),
        country: this.getUserCountry(),
        region: this.getUserRegion(),
        city: this.getUserCity()
      };

      console.log('📊 Invio evento analytics:', enrichedEventData);

      const response = await API.post(`${this.baseURL}/track`, enrichedEventData);
      
      if (response.data.success) {
        console.log(`✅ Evento analytics inviato: ${eventData.event_type} per seller ${eventData.seller_id}`);
        return response.data;
      } else {
        console.error('❌ Errore nell\'invio evento analytics:', response.data.message);
        return null;
      }
    } catch (error) {
      console.error('❌ Errore nella chiamata API analytics:', error);
      return null;
    }
  }

  /**
   * Traccia impression di prodotto (card visibile)
   */
  async trackProductImpression(productData, source = 'other') {
    return this.trackEvent({
      seller_id: productData.seller_id,
      event_type: 'product_impression',
      product_id: productData.id,
      product_name: productData.title || productData.name,
      product_price: parseFloat(productData.price_min || productData.price || 0),
      product_category: productData.product_type || productData.category,
      impression_source: source
    });
  }

  /**
   * Traccia click su prodotto (da card a pagina prodotto)
   */
  async trackProductClick(productData, source = 'other') {
    return this.trackEvent({
      seller_id: productData.seller_id,
      event_type: 'product_click',
      product_id: productData.id,
      product_name: productData.title || productData.name,
      product_price: parseFloat(productData.price_min || productData.price || 0),
      product_category: productData.product_type || productData.category,
      impression_source: source
    });
  }

  /**
   * Traccia visualizzazione pagina prodotto
   */
  async trackProductView(productData, source = 'other') {
    return this.trackEvent({
      seller_id: productData.seller_id,
      event_type: 'product_view',
      product_id: productData.id,
      product_name: productData.title || productData.name,
      product_price: parseFloat(productData.price_min || productData.price || 0),
      product_category: productData.product_type || productData.category,
      impression_source: source
    });
  }

  /**
   * Traccia click verso sito esterno del seller
   */
  async trackExternalClick(productData, source = 'other') {
    return this.trackEvent({
      seller_id: productData.seller_id,
      event_type: 'external_click',
      product_id: productData.id,
      product_name: productData.title || productData.name,
      product_price: parseFloat(productData.price_min || productData.price || 0),
      product_category: productData.product_type || productData.category,
      impression_source: source
    });
  }

  /**
   * Traccia aggiunta alla wishlist
   */
  async trackWishlistAdd(productData, source = 'other') {
    return this.trackEvent({
      seller_id: productData.seller_id,
      event_type: 'wishlist_add',
      product_id: productData.id,
      product_name: productData.title || productData.name,
      product_price: parseFloat(productData.price_min || productData.price || 0),
      product_category: productData.product_type || productData.category,
      impression_source: source
    });
  }

  /**
   * Determina la sorgente basandosi sull'URL corrente
   */
  determineSource() {
    const path = window.location.pathname;
    
    if (path.includes('/category/') || path.includes('/categories/')) {
      return 'category_page';
    } else if (path.includes('/search') || window.location.search.includes('q=')) {
      return 'search_results';
    } else if (path.includes('/brand/') || path.includes('/brands/')) {
      return 'brand_page';
    } else if (path.includes('/seller/')) {
      return 'seller_page';
    } else if (path.includes('/product/') || path.includes('/products/')) {
      return 'product_page';
    } else if (path === '/' || path === '/home') {
      return 'homepage';
    } else {
      return 'other';
    }
  }

  /**
   * Ottiene o genera un session ID
   */
  getSessionId() {
    let sessionId = localStorage.getItem('loverlist_session_id');
    
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
      localStorage.setItem('loverlist_session_id', sessionId);
    }
    
    return sessionId;
  }

  /**
   * Ottiene il paese dell'utente (da implementare con geolocalizzazione)
   */
  getUserCountry() {
    // Per ora restituisce IT, in futuro si può implementare geolocalizzazione
    return 'IT';
  }

  /**
   * Ottiene la regione dell'utente
   */
  getUserRegion() {
    return '';
  }

  /**
   * Ottiene la città dell'utente
   */
  getUserCity() {
    return '';
  }

  /**
   * Ottiene statistiche di un seller
   */
  async getSellerStats(params = {}) {
    try {
      const { dateRange = '7d', groupBy = 'day' } = params;
      
      const response = await API.get(`${this.baseURL}/seller/stats`, {
        params: {
          dateRange,
          groupBy
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Errore nel recupero statistiche seller:', error);
      throw error;
    }
  }

  /**
   * Ottiene metriche di performance di un seller
   */
  async getSellerMetrics(params = {}) {
    try {
      const { dateRange = '7d', groupBy = 'day' } = params;
      
      const response = await API.get(`${this.baseURL}/seller/metrics`, {
        params: {
          dateRange,
          groupBy
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('❌ Errore nel recupero metriche seller:', error);
      throw error;
    }
  }
}

// Singleton instance
const sellerAnalyticsService = new SellerAnalyticsService();

export default sellerAnalyticsService; 